import { useState, useEffect, useRef } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { MapPin, Loader2 } from 'lucide-react';
import { geocodeAddress, type AddressSuggestion } from '@/api/schools';

interface AddressAutocompleteProps {
  value: string;
  country?: string;
  onChange: (address: string) => void;
  onSelect?: (suggestion: AddressSuggestion) => void;
  placeholder?: string;
  id?: string;
  disabled?: boolean;
  rows?: number;
}

export function AddressAutocomplete({
  value,
  country = 'FR',
  onChange,
  onSelect,
  placeholder = 'Saisir une adresse...',
  id,
  disabled,
  rows = 3,
}: AddressAutocompleteProps) {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const containerRef = useRef<HTMLDivElement>(null);
  const skipSearchRef = useRef(false);

  // Sync external value changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Close suggestions on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  useEffect(() => {
    if (skipSearchRef.current) {
      skipSearchRef.current = false;
      return;
    }

    // Use first line only for geocoding search
    const searchText = inputValue.split('\n')[0].trim();

    if (searchText.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setLoading(true);
      try {
        const results = await geocodeAddress(searchText, country);
        setSuggestions(results);
        setShowSuggestions(results.length > 0);
      } catch (err) {
        console.error('Address search error:', err);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [inputValue, country]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);
  };

  const handleSelect = (suggestion: AddressSuggestion) => {
    skipSearchRef.current = true;
    setInputValue(suggestion.label);
    onChange(suggestion.label);
    setShowSuggestions(false);
    setSuggestions([]);
    onSelect?.(suggestion);
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Textarea
          id={id}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          placeholder={placeholder}
          disabled={disabled}
          rows={rows}
        />
        <div className="absolute right-3 top-3">
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : (
            <MapPin className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              type="button"
              className="w-full px-3 py-2 text-left hover:bg-muted border-b last:border-b-0"
              onClick={() => handleSelect(suggestion)}
            >
              <div className="flex items-start gap-2">
                <MapPin className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm truncate">{suggestion.label}</div>
                  {suggestion.context && (
                    <div className="text-xs text-muted-foreground truncate">
                      {suggestion.context}
                    </div>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
